package features.watch

import com.kinoshazam.WATCH_PROGRESS_OWNER
import com.kinoshazam.analytics.userIdFromJwt
import com.kinoshazam.data.GlobalStateRepository
import com.kinoshazam.data.Storage
import com.kinoshazam.data.TokenRepository
import data.VideoRepository
import data.db.delight.daos.WatchProgressDao
import data.db.delight.daos.toEntry
import features.watch.data.WatchProgressRepository
import features.watch.model.PlaybackChoice
import features.watch.model.WatchEntry
import features.watch.model.WatchTarget
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.emitAll
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import models.VideoDetails
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

class WatchProgressInteractor(
    private val dao: WatchProgressDao,
    private val repository: WatchProgressRepository,
    private val videos: VideoRepository,
    private val globalStateRepository: GlobalStateRepository,
    private val storage: Storage,
    private val tokenRepository: TokenRepository,
) {
    private val mutex = Mutex()

    fun observe(): Flow<List<WatchEntry>> = flow {
        val source = dao.observe()
        if (source == null) {
            emit(emptyList())
        } else {
            emitAll(source.map { rows -> rows.map { it.toEntry() } })
        }
    }

    suspend fun playbackTarget(videoId: Int): WatchTarget? {
        val rows = dao.snapshot().filter { it.videoIdInt == videoId && !it.isDeleted && !it.hiddenFromContinue }
        val active = rows
            .filter(WatchRules::visibleInContinue)
            .maxByOrNull { it.changedAt }
        if (active != null) {
            return WatchTarget(
                seasonNumber = active.seasonNumber,
                episodeNumber = active.episodeNumber,
                positionSec = active.positionSec,
                completed = false,
                queuedNext = active.isTvShow && active.positionSec <= 0 && active.engagedSec <= 0,
            )
        }
        val finishedMovie = rows.firstOrNull {
            !it.isTvShow && it.status == WatchEntry.STATUS_COMPLETED
        }
        if (finishedMovie != null) {
            return WatchTarget(
                seasonNumber = 0,
                episodeNumber = 0,
                positionSec = 0,
                completed = true,
                queuedNext = false,
            )
        }
        return null
    }

    suspend fun resumeSeconds(videoId: Int, isTvShow: Boolean, seasonNumber: Int, episodeNumber: Int): Double {
        if (videoId <= 0) return 0.0
        val season = if (isTvShow) seasonNumber else 0
        val episode = if (isTvShow) episodeNumber else 0
        val row = dao.find(videoId, season, episode) ?: return 0.0
        if (row.isDeleted || row.status == WatchEntry.STATUS_COMPLETED) return 0.0
        if (row.progress >= WatchRules.COMPLETE_FRACTION) return 0.0
        if (row.positionSec < 5) return 0.0
        return row.positionSec.toDouble()
    }

    suspend fun savedChoice(videoId: Int, isTvShow: Boolean, seasonNumber: Int, episodeNumber: Int): PlaybackChoice {
        if (videoId <= 0) return PlaybackChoice()
        val season = if (isTvShow) seasonNumber else 0
        val episode = if (isTvShow) episodeNumber else 0
        val row = dao.find(videoId, season, episode) ?: return PlaybackChoice()
        if (row.isDeleted) return PlaybackChoice()
        return PlaybackChoice(
            audio = row.audio.remembered(),
            quality = row.quality.remembered(),
            subtitles = row.subtitles.remembered(),
        )
    }

    @OptIn(ExperimentalTime::class)
    suspend fun record(
        videoId: Int,
        imdbId: String,
        isTvShow: Boolean,
        seasonNumber: Int,
        episodeNumber: Int,
        title: String,
        imageUrl: String,
        backdropUrl: String,
        positionSec: Int,
        durationSec: Int,
        engagedDeltaSec: Int,
        audio: String? = null,
        quality: String? = null,
        subtitles: String? = null,
    ) {
        if (videoId <= 0 || durationSec <= 0) return
        val season = if (isTvShow) seasonNumber else 0
        val episode = if (isTvShow) episodeNumber else 0
        mutex.withLock {
            val userId = sessionUserId() ?: return@withLock
            alignCache(userId)
            val existing = dao.find(videoId, season, episode)
            val engaged = (existing?.engagedSec ?: 0) + engagedDeltaSec.coerceAtLeast(0)
            val progress = (positionSec.toDouble() / durationSec.toDouble()).coerceIn(0.0, 1.0)
            val qualified = WatchRules.qualified(engaged, durationSec)
            val completed = qualified && progress >= WatchRules.COMPLETE_FRACTION
            val wasCompleted = existing?.status == WatchEntry.STATUS_COMPLETED
            val now = Clock.System.now().toString()
            val row = WatchEntry(
                videoId = videoId.toString(),
                imdbId = imdbId.ifBlank { existing?.imdbId },
                isTvShow = isTvShow,
                seasonNumber = season,
                episodeNumber = episode,
                positionSec = if (completed) durationSec else positionSec,
                durationSec = durationSec,
                progress = if (completed) 1.0 else progress,
                engagedSec = engaged,
                status = if (completed) WatchEntry.STATUS_COMPLETED else WatchEntry.STATUS_IN_PROGRESS,
                title = title.ifBlank { existing?.title },
                imageUrl = imageUrl.ifBlank { existing?.imageUrl },
                backdropUrl = backdropUrl.ifBlank { existing?.backdropUrl },
                audio = audio.remembered() ?: existing?.audio,
                quality = quality.remembered() ?: existing?.quality,
                subtitles = subtitles.remembered() ?: existing?.subtitles,
                hiddenFromContinue = if (engagedDeltaSec > 0) false else existing?.hiddenFromContinue == true,
                isDeleted = false,
                changedAt = now,
            )
            dao.upsert(row)
            if (WatchRules.shouldSync(row)) push(listOf(row))
            if (completed && isTvShow && !wasCompleted) {
                advanceToNext(row)
            }
        }
    }

    @OptIn(ExperimentalTime::class)
    suspend fun hideFromContinue(entry: WatchEntry) {
        mutex.withLock {
            if (!alignCache(sessionUserId())) return@withLock
            val updated = entry.copy(
                hiddenFromContinue = true,
                changedAt = Clock.System.now().toString(),
            )
            dao.upsert(updated)
            push(listOf(updated))
        }
    }

    @OptIn(ExperimentalTime::class)
    suspend fun delete(entry: WatchEntry) {
        mutex.withLock {
            if (!alignCache(sessionUserId())) return@withLock
            val updated = entry.copy(
                isDeleted = true,
                changedAt = Clock.System.now().toString(),
            )
            dao.upsert(updated)
            push(listOf(updated))
        }
    }

    @OptIn(ExperimentalTime::class)
    suspend fun clearHistory() {
        mutex.withLock {
            if (!alignCache(sessionUserId())) return@withLock
            val now = Clock.System.now().toString()
            val updated = dao.snapshot()
                .filter { !it.isDeleted }
                .map { it.copy(isDeleted = true, changedAt = now) }
            if (updated.isEmpty()) return
            updated.forEach { dao.upsert(it) }
            push(updated)
        }
    }

    /**
     * Logout drops the local mirror. Login replaces it with this account's server
     * history and does not upload whatever was stored for someone else.
     */
    suspend fun bindSession(userId: String?) {
        mutex.withLock { alignCache(userId) }
        if (userId == null) return
        syncOwned(userId)
    }

    /** Drop another account's rows before profile is ready. Does not upload or claim the cache. */
    suspend fun discardIfForeign(userId: String) {
        mutex.withLock {
            val owner = storage.get<String>(WATCH_PROGRESS_OWNER)
            if (watchCacheDecision(owner, userId) == WatchCacheDecision.DROP) {
                dao.replaceAll(emptyList())
            }
        }
    }

    private suspend fun syncOwned(userId: String) {
        if (sessionUserId() != userId) return
        val language = globalStateRepository.appLanguage.value.locale
        mutex.withLock {
            if (storage.get<String>(WATCH_PROGRESS_OWNER) != userId) return
            if (sessionUserId() != userId) return
        }
        val remote = runCatching { repository.sync(emptyList(), language) }.getOrNull() ?: return
        mutex.withLock {
            if (storage.get<String>(WATCH_PROGRESS_OWNER) != userId) return
            if (sessionUserId() != userId) return
            dao.replaceAll(merge(dao.snapshot(), remote))
        }
    }

    private suspend fun push(items: List<WatchEntry>) {
        val userId = sessionUserId() ?: return
        if (storage.get<String>(WATCH_PROGRESS_OWNER) != userId) return
        runCatching { repository.sync(items) }
    }

    private fun sessionUserId(): String? {
        if (!globalStateRepository.isAuthorized.value) return null
        return tokenRepository.getAccessToken()?.let(::userIdFromJwt)
    }

    /** @return false when the previous account's rows were discarded */
    private suspend fun alignCache(userId: String?): Boolean {
        val owner = storage.get<String>(WATCH_PROGRESS_OWNER)
        val keep = watchCacheDecision(owner, userId) == WatchCacheDecision.KEEP
        if (!keep) dao.replaceAll(emptyList())
        when {
            userId != null -> storage.put(WATCH_PROGRESS_OWNER, userId)
            !keep -> storage.remove(WATCH_PROGRESS_OWNER)
        }
        return keep
    }

    @OptIn(ExperimentalTime::class)
    private suspend fun advanceToNext(completed: WatchEntry) {
        val videoId = completed.videoIdInt
        val next = resolveNext(videoId, completed.seasonNumber, completed.episodeNumber) ?: return
        val existing = dao.find(videoId, next.first, next.second)
        if (existing != null && !existing.isDeleted) return
        val queued = WatchEntry(
            videoId = completed.videoId,
            imdbId = completed.imdbId,
            isTvShow = true,
            seasonNumber = next.first,
            episodeNumber = next.second,
            positionSec = 0,
            durationSec = 0,
            progress = 0.0,
            engagedSec = 0,
            status = WatchEntry.STATUS_IN_PROGRESS,
            title = completed.title,
            imageUrl = completed.imageUrl,
            backdropUrl = completed.backdropUrl,
            audio = completed.audio,
            quality = completed.quality,
            subtitles = completed.subtitles,
            hiddenFromContinue = false,
            isDeleted = false,
            changedAt = Clock.System.now().toString(),
        )
        dao.upsert(queued)
        push(listOf(queued))
    }

    private suspend fun resolveNext(videoId: Int, season: Int, episode: Int): Pair<Int, Int>? {
        val current = runCatching { videos.getTvSeasonDetails(videoId, season) }.getOrNull()
        val nextInSeason = current?.episodes.orEmpty()
            .filter { it.episodeNumber > episode }
            .minByOrNull { it.episodeNumber }
        if (nextInSeason != null) return season to nextInSeason.episodeNumber
        val show = runCatching { videos.getTvShowDetails(videoId) }.getOrNull()
        val nextSeasonNumber = show?.seasons.orEmpty()
            .map(VideoDetails.Season::seasonNumber)
            .filter { it > season }
            .minOrNull() ?: return null
        val nextSeason = runCatching { videos.getTvSeasonDetails(videoId, nextSeasonNumber) }.getOrNull()
        val first = nextSeason?.episodes.orEmpty().minByOrNull { it.episodeNumber } ?: return null
        return nextSeasonNumber to first.episodeNumber
    }
}

private fun String?.remembered(): String? = this?.trim()?.takeIf { it.isNotEmpty() }

private fun WatchEntry.identityKey(): String = "$videoId:$seasonNumber:$episodeNumber"

private fun WatchEntry.withChoiceFrom(other: WatchEntry?): WatchEntry {
    if (other == null) return this
    return copy(
        audio = audio ?: other.audio,
        quality = quality ?: other.quality,
        subtitles = subtitles ?: other.subtitles,
    )
}

private fun merge(current: List<WatchEntry>, remote: List<WatchEntry>): List<WatchEntry> {
    val currentById = current.associateBy { it.identityKey() }
    val seen = mutableSetOf<String>()
    val merged = ArrayList<WatchEntry>(remote.size + current.size)
    for (row in remote) {
        val key = row.identityKey()
        seen += key
        val localRow = currentById[key]
        merged += if (localRow != null && localRow.changedAt > row.changedAt) {
            localRow.withChoiceFrom(row)
        } else {
            row.withChoiceFrom(localRow)
        }
    }
    for (row in current) {
        if (row.identityKey() in seen || row.isDeleted) continue
        merged += row
    }
    return merged
}
